import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Alert,
    LinearProgress,
    Pagination,
    Tooltip,
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    GetApp as DownloadIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import MainCard from 'ui-component/cards/MainCard';
import { useParams } from 'react-router-dom';
import { GET_ME } from 'graphql/queries';

const GET_AUDIT_LOGS = gql`
    query GetAuditLogs($eventId: String!, $filters: AuditLogFilters, $limit: Int, $offset: Int) {
        getAuditLogs(eventId: $eventId, filters: $filters, limit: $limit, offset: $offset) {
            id
            eventId
            userId
            userEmail
            userName
            userRole
            action
            resourceType
            resourceId
            resourceName
            changes
            oldData
            newData
            ipAddress
            userAgent
            additionalInfo
            created
        }
    }
`;

const GET_AUDIT_LOG_COUNT = gql`
    query GetAuditLogCount($eventId: String!, $filters: AuditLogFilters) {
        getAuditLogCount(eventId: $eventId, filters: $filters)
    }
`;

const GET_AUDIT_LOG_STATS = gql`
    query GetAuditLogStats($eventId: String!, $dateRange: String!) {
        getAuditLogStats(eventId: $eventId, dateRange: $dateRange) {
            totalActions
            actionsByType
            actionsByUser
            actionsByResource
            recentActivity {
                id
                userId
                userName
                action
                resourceType
                resourceName
                created
            }
        }
    }
`;

export default function AuditLogDashboard() {
    const { eventId } = useParams();
    const { data: userData } = useQuery(GET_ME);

    // Check if user is superadmin
    if (!userData?.me?.permissions?.includes('superadmin')) {
        return (
            <MainCard title='Audit Log Dashboard'>
                <Alert severity='error'>Access denied. Superadmin only.</Alert>
            </MainCard>
        );
    }

    return <AuditLogContent eventId={eventId} />;
}

function AuditLogContent({ eventId }) {
    const [filters, setFilters] = useState({
        userId: '',
        action: '',
        resourceType: '',
        startDate: '',
        endDate: '',
        searchTerm: '',
    });
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [selectedLog, setSelectedLog] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);

    const { loading, error, data, refetch } = useQuery(GET_AUDIT_LOGS, {
        variables: {
            eventId,
            filters: Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
            limit,
            offset: (page - 1) * limit,
        },
        fetchPolicy: 'network-only',
    });

    const { data: countData } = useQuery(GET_AUDIT_LOG_COUNT, {
        variables: {
            eventId,
            filters: Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
        },
    });

    const { data: statsData } = useQuery(GET_AUDIT_LOG_STATS, {
        variables: { eventId, dateRange: '7d' },
    });

    const logs = data?.getAuditLogs || [];
    const totalCount = countData?.getAuditLogCount || 0;
    const stats = statsData?.getAuditLogStats;

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1); // Reset to first page on filter change
    };

    const handleViewDetails = log => {
        setSelectedLog(log);
        setDetailDialogOpen(true);
    };

    const getActionColor = action => {
        const colors = {
            create: 'success',
            update: 'info',
            delete: 'error',
            login: 'primary',
            logout: 'secondary',
        };
        return colors[action] || 'default';
    };

    const totalPages = Math.ceil(totalCount / limit);

    return (
        <Box>
            <MainCard title='Audit Log Dashboard'>
                {/* Statistics Cards */}
                {stats && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant='h4' color='primary'>
                                        {stats.totalActions}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Total Actions (7 days)
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant='h4' color='success.main'>
                                        {Object.keys(stats.actionsByType || {}).length}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Action Types
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant='h4' color='info.main'>
                                        {stats.actionsByUser?.length || 0}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Active Users
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant='h4' color='warning.main'>
                                        {Object.keys(stats.actionsByResource || {}).length}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Resource Types
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Filters */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Stack spacing={2}>
                        <Typography variant='h6'>Filters</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    fullWidth
                                    label='Search'
                                    value={filters.searchTerm}
                                    onChange={e => handleFilterChange('searchTerm', e.target.value)}
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth>
                                    <InputLabel>Action</InputLabel>
                                    <Select
                                        value={filters.action}
                                        label='Action'
                                        onChange={e => handleFilterChange('action', e.target.value)}
                                    >
                                        <MenuItem value=''>All</MenuItem>
                                        <MenuItem value='create'>Create</MenuItem>
                                        <MenuItem value='update'>Update</MenuItem>
                                        <MenuItem value='delete'>Delete</MenuItem>
                                        <MenuItem value='login'>Login</MenuItem>
                                        <MenuItem value='logout'>Logout</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    label='Start Date'
                                    type='date'
                                    value={filters.startDate}
                                    onChange={e => handleFilterChange('startDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    label='End Date'
                                    type='date'
                                    value={filters.endDate}
                                    onChange={e => handleFilterChange('endDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Button
                                    variant='outlined'
                                    startIcon={<FilterIcon />}
                                    onClick={() => {
                                        setFilters({
                                            userId: '',
                                            action: '',
                                            resourceType: '',
                                            startDate: '',
                                            endDate: '',
                                            searchTerm: '',
                                        });
                                        setPage(1);
                                    }}
                                    fullWidth
                                >
                                    Clear Filters
                                </Button>
                            </Grid>
                        </Grid>
                    </Stack>
                </Paper>

                {/* Audit Logs Table */}
                {loading && <LinearProgress />}
                {error && (
                    <Alert severity='error' sx={{ mb: 2 }}>
                        Error loading audit logs: {error.message}
                    </Alert>
                )}

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Action</TableCell>
                                <TableCell>Resource</TableCell>
                                <TableCell>Details</TableCell>
                                <TableCell>IP Address</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map(log => (
                                <TableRow key={log.id} hover>
                                    <TableCell>
                                        {log.created
                                            ? format(new Date(log.created), 'PPpp')
                                            : 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant='body2' fontWeight={600}>
                                                {log.userName || log.userEmail || log.userId}
                                            </Typography>
                                            {log.userRole && (
                                                <Chip label={log.userRole} size='small' sx={{ mt: 0.5 }} />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.action}
                                            color={getActionColor(log.action)}
                                            size='small'
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box>
                                            <Typography variant='body2'>{log.resourceType}</Typography>
                                            {log.resourceName && (
                                                <Typography variant='caption' color='text.secondary'>
                                                    {log.resourceName}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {log.changes && (
                                            <Tooltip title='View changes'>
                                                <Chip label='Has Changes' size='small' variant='outlined' />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>{log.ipAddress || 'N/A'}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            size='small'
                                            onClick={() => handleViewDetails(log)}
                                            color='primary'
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            color='primary'
                        />
                    </Box>
                )}

                {/* Detail Dialog */}
                <Dialog
                    open={detailDialogOpen}
                    onClose={() => setDetailDialogOpen(false)}
                    maxWidth='md'
                    fullWidth
                >
                    <DialogTitle>
                        <Stack direction='row' justifyContent='space-between' alignItems='center'>
                            <Typography variant='h6'>Audit Log Details</Typography>
                            <IconButton onClick={() => setDetailDialogOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </DialogTitle>
                    <DialogContent>
                        {selectedLog && (
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        User Information
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>Name:</strong> {selectedLog.userName || 'N/A'}
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>Email:</strong> {selectedLog.userEmail || 'N/A'}
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>Role:</strong> {selectedLog.userRole || 'N/A'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Action Details
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>Action:</strong>{' '}
                                        <Chip
                                            label={selectedLog.action}
                                            color={getActionColor(selectedLog.action)}
                                            size='small'
                                        />
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>Resource Type:</strong> {selectedLog.resourceType}
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>Resource ID:</strong> {selectedLog.resourceId || 'N/A'}
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>Resource Name:</strong> {selectedLog.resourceName || 'N/A'}
                                    </Typography>
                                </Box>

                                {selectedLog.changes && (
                                    <Box>
                                        <Typography variant='subtitle2' color='text.secondary'>
                                            Changes
                                        </Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                            <pre style={{ fontSize: '0.875rem', margin: 0 }}>
                                                {JSON.stringify(JSON.parse(selectedLog.changes), null, 2)}
                                            </pre>
                                        </Paper>
                                    </Box>
                                )}

                                {selectedLog.oldData && (
                                    <Box>
                                        <Typography variant='subtitle2' color='text.secondary'>
                                            Old Data
                                        </Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                            <pre style={{ fontSize: '0.875rem', margin: 0 }}>
                                                {JSON.stringify(JSON.parse(selectedLog.oldData), null, 2)}
                                            </pre>
                                        </Paper>
                                    </Box>
                                )}

                                {selectedLog.newData && (
                                    <Box>
                                        <Typography variant='subtitle2' color='text.secondary'>
                                            New Data
                                        </Typography>
                                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                                            <pre style={{ fontSize: '0.875rem', margin: 0 }}>
                                                {JSON.stringify(JSON.parse(selectedLog.newData), null, 2)}
                                            </pre>
                                        </Paper>
                                    </Box>
                                )}

                                <Box>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Technical Details
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>IP Address:</strong> {selectedLog.ipAddress || 'N/A'}
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>User Agent:</strong> {selectedLog.userAgent || 'N/A'}
                                    </Typography>
                                    <Typography variant='body1'>
                                        <strong>Timestamp:</strong>{' '}
                                        {selectedLog.created
                                            ? format(new Date(selectedLog.created), 'PPpp')
                                            : 'N/A'}
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </MainCard>
        </Box>
    );
}

